import {
  Anchor,
  Container,
  Divider,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
  useMantineTheme,
} from "@mantine/core"
import { useForm, zodResolver } from "@mantine/form"
import { IconArrowBackUp, IconCircleCheck } from "@tabler/icons-react"
import { Button, Card, ErrorBox, LogoDark, Loader } from "@tfg/ui"
import { createRef, useEffect, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"

import { useAuth } from "~/store/auth-context"

import {
  LoginForm,
  loginSchema,
  TwoFactorForm,
  twoFactorSchema,
  useForgotPasswordMutation,
  useLogin,
  useMagicLinkMutation,
  useVerifyTwoFactorMutation,
} from "./hooks"

const publicKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY

type LoginState =
  | "login"
  | "forgotPassword"
  | "resetPasswordEmailSent"
  | "magicLink"
  | "magicLinkEmailSent"
  | "twoFactor"

export const Login = () => {
  const { colors } = useMantineTheme()
  const { t } = useTranslation("account")
  const navigate = useNavigate()
  const { isLoading, isAuthenticated, authenticate } = useAuth()

  const [pageState, setPageState] = useState<LoginState>("login")
  const [email, setEmail] = useState<string>("")
  const [userId, setUserId] = useState<string>("")

  const [queryParams] = useSearchParams()
  const redirected = queryParams.get("redirected")

  const recaptchaRef = createRef<ReCAPTCHA>()

  useEffect(() => {
    if (redirected === "supplier") {
      setPageState("magicLink")
    }
  }, [redirected])

  useEffect(() => {
    if (isAuthenticated) navigate("/")
  }, [navigate, isAuthenticated])

  const form = useForm<LoginForm>({
    validate: zodResolver(loginSchema),
  })

  const twoFactorForm = useForm<TwoFactorForm>({
    validate: zodResolver(twoFactorSchema),
    initialValues: {
      token: "",
      userId: "",
    },
  })

  const handleLogin = async (values: LoginForm) => {
    let recaptchaToken: string | undefined
    if (import.meta.env.VITE_RECAPTCHA_ENABLED === "true") {
      if (import.meta.env.DEV) {
        recaptchaToken = "dev"
      } else {
        recaptchaToken =
          (await recaptchaRef.current?.executeAsync()) ?? undefined
      }

      if (!recaptchaToken) {
        throw new Error("Recaptcha token not found")
      }
    }

    try {
      const response = await login({ ...values, recaptchaToken })
      if (!response.twoFactor) {
        if (!response.user) throw new Error("User not found")
        authenticate(response.user)
        navigate("/")
        setPageState("login")
        return
      } else {
        if (!response.userId) throw new Error("User id not found")
        setPageState("twoFactor")
        setUserId(response.userId)
      }
    } catch (e) {
      console.error(e)
    }
  }

  const handleVerifyTwoFactor = async (values: TwoFactorForm) => {
    try {
      const user = await verifyTwoFactor(values)
      authenticate(user)
      navigate("/")
      setPageState("login")
    } catch (e) {
      console.error(e)
    }
  }

  const {
    mutateAsync: login,
    isLoading: isLoggingIn,
    error: loginError,
  } = useLogin()

  const { mutateAsync: verifyTwoFactor } = useVerifyTwoFactorMutation({
    onSuccess: (data) => {
      authenticate(data)
      navigate("/")
      setPageState("login")
    },
  })

  const {
    mutateAsync: sendResetPasswordEmail,
    isLoading: isSendingResetPasswordEmail,
    error: forgotPasswordError,
  } = useForgotPasswordMutation({
    onSuccess: () => setPageState("resetPasswordEmailSent"),
  })

  const {
    mutateAsync: sendMagicLinkEmail,
    isLoading: isSendingMagicLinkdEmail,
    error: magicLinkError,
  } = useMagicLinkMutation({
    onSuccess: () => setPageState("magicLinkEmailSent"),
  })

  if (isLoading) return <Loader fullPage />
  return (
    <Container
      fluid
      bg="var(--mantine-color-brand-background)"
      w="100vw"
      h="100vh"
    >
      <Group mb={100} align="center" justify="center" h="100vh">
        <Card px="2rem" py="3rem" maw={400}>
          {pageState === "login" ? (
            <form onSubmit={form.onSubmit((values) => handleLogin(values))}>
              {import.meta.env.VITE_RECAPTCHA_ENABLED === "true" && (
                <ReCAPTCHA
                  sitekey={publicKey}
                  size="invisible"
                  ref={recaptchaRef}
                />
              )}
              <Card.Section>
                <LogoDark width="20rem" />
                <Title
                  order={3}
                  size="xs"
                  fw={500}
                  style={{ textAlign: "center" }}
                >
                  {t("login.title")}
                </Title>
                <ErrorBox error={loginError} />
                <TextInput
                  type="email"
                  label={t("login.labels.email")}
                  placeholder={t("login.placeholders.email")}
                  autoFocus
                  {...form.getInputProps("username")}
                />
                <Stack
                  align="right"
                  w="100%"
                  gap="var(--tfg-molecule-padding-y)"
                >
                  <TextInput
                    type="password"
                    label={t("login.labels.password")}
                    placeholder={t("login.placeholders.password")}
                    {...form.getInputProps("password")}
                  />
                  <Anchor
                    style={{ textAlign: "right" }}
                    size="xs"
                    c="green"
                    onClick={() => setPageState("forgotPassword")}
                  >
                    {t("login.forgotPassword.button")}
                  </Anchor>
                </Stack>
                <Button
                  type="submit"
                  variant="primary"
                  fullWidth={true}
                  loading={isLoggingIn}
                >
                  {t("login.submit")}
                </Button>

                <Divider />
                <Button
                  variant="secondary"
                  fullWidth={true}
                  onClick={() => setPageState("magicLink")}
                >
                  {t("login.imasupplier")}
                </Button>
              </Card.Section>
            </form>
          ) : pageState === "twoFactor" ? (
            <Card.Section>
              <LogoDark width="20rem" />

              <form
                onSubmit={twoFactorForm.onSubmit((values) =>
                  handleVerifyTwoFactor({ ...values, userId })
                )}
              >
                <TextInput
                  type="text"
                  label="Fill the generated code"
                  autoFocus
                  {...twoFactorForm.getInputProps("token")}
                />
                <Button
                  mt="1rem"
                  type="submit"
                  fullWidth={true}
                  disabled={!twoFactorForm.isValid()}
                >
                  Login
                </Button>
              </form>
            </Card.Section>
          ) : pageState === "forgotPassword" ? (
            <Card.Section>
              <LogoDark width="20rem" />
              <Text style={{ textAlign: "center" }}>
                {t("login.forgotPassword.title")}
              </Text>
              <ErrorBox error={forgotPasswordError} />
              <TextInput
                required
                type="email"
                autoFocus
                value={email}
                label={t("login.labels.email")}
                placeholder={t("login.placeholders.email")}
                onChange={(e) => setEmail(e.currentTarget.value)}
              />
              <Group mt="1rem" justify="center">
                <Button
                  fullWidth={true}
                  disabled={!email}
                  loading={isSendingResetPasswordEmail}
                  onClick={() => sendResetPasswordEmail({ email })}
                >
                  {t("login.forgotPassword.submit")}
                </Button>
                <Button
                  fullWidth={true}
                  variant="secondary"
                  disabled={isSendingResetPasswordEmail}
                  onClick={() => setPageState("login")}
                >
                  {t("login.forgotPassword.back")}
                </Button>
              </Group>
            </Card.Section>
          ) : pageState === "magicLink" ? (
            <Stack>
              <LogoDark width="20rem" />
              <Text style={{ textAlign: "center" }}>
                {t("login.magicLink.title")}
              </Text>
              <ErrorBox error={magicLinkError} />
              <TextInput
                required
                type="email"
                autoFocus
                value={email}
                label={t("login.labels.email")}
                placeholder={t("login.placeholders.email")}
                onChange={(e) => setEmail(e.currentTarget.value)}
              />
              <Group mt="1rem" justify="center">
                <Button
                  disabled={!email}
                  fullWidth={true}
                  loading={isSendingMagicLinkdEmail}
                  onClick={() => {
                    sendMagicLinkEmail({ email })
                  }}
                >
                  {t("login.magicLink.submit")}
                </Button>
                <Button
                  variant="tertiary"
                  fullWidth={true}
                  disabled={isSendingMagicLinkdEmail}
                  onClick={() => setPageState("login")}
                >
                  <IconArrowBackUp strokeWidth={1.5} />
                  {t("login.magicLink.back")}
                </Button>
              </Group>
            </Stack>
          ) : pageState === "resetPasswordEmailSent" ? (
            <Stack align="center">
              <LogoDark width="20rem" />
              <Text style={{ textAlign: "center" }}>
                {t("login.forgotPassword.success")}
              </Text>
              <Stack my="0.5rem">
                <IconCircleCheck size="6rem" color={colors.green[6]} />
              </Stack>
              <Button
                disabled={isSendingResetPasswordEmail}
                onClick={() => setPageState("login")}
              >
                {t("login.forgotPassword.back")}
              </Button>
            </Stack>
          ) : (
            <Stack align="center">
              <LogoDark width="20rem" />
              <Text style={{ textAlign: "center" }}>
                {t("login.magicLink.success")}
              </Text>
              <Stack my="0.5rem">
                <IconCircleCheck size="6rem" color={colors.green[6]} />
              </Stack>
              <Button
                disabled={isSendingResetPasswordEmail}
                onClick={() => setPageState("login")}
              >
                {t("login.magicLink.back")}
              </Button>
            </Stack>
          )}
          {import.meta.env.VITE_RECAPTCHA_ENABLED === "true" && (
            <Text style={{ textAlign: "center" }} mt="1rem">
              This site is protected by reCAPTCHA and the Google{" "}
              <a href="https://policies.google.com/privacy">Privacy Policy</a>{" "}
              and{" "}
              <a href="https://policies.google.com/terms">Terms of Service</a>{" "}
              apply.
            </Text>
          )}
        </Card>
      </Group>
    </Container>
  )
}
